import { useState } from 'react'
import { fmtDate, fmtDistanceToNow } from '@/lib/dates'
import {
  ArrowLeft,
  Play,
  Pause,
  Users,
  Send,
  MessageSquare,
  Calendar,
  Activity,
  Trash2,
  Pencil,
  UserPlus,
  Settings2,
  Sparkles,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useCampaign, useCampaignStats, useCampaignLeads, useActivateCampaign, usePauseCampaign, useRemoveLeadFromCampaign } from './use-campaigns'
import { TestModeButton } from './test-mode-button'
import type { Campaign } from './types'
import { STEP_TYPE_LABELS, STRATEGY_LABELS } from './constants'
import { EditCampaignDialog } from './edit-campaign-dialog'
import { AddLeadsDialog } from './add-leads-dialog'
import { EditSequenceDialog } from './edit-sequence-dialog'
import { DeleteCampaignDialog } from './delete-campaign-dialog'
import { PreviewMessagesDialog } from './preview-messages-dialog'
import { PlanGate } from '@/components/plan-gate'
import { usePlan } from '@/hooks/use-plan'
import { useUser } from '@clerk/clerk-react'
import { isTahaOnly } from '@/features/admin/staff'

type StatCardProps = {
  readonly label: string
  readonly value: string | number
  readonly icon: React.ReactNode
  readonly sub?: string
}

function StatCard({ label, value, icon, sub }: StatCardProps) {
  return (
    <Card>
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className="text-2xl font-bold mt-0.5">{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className="text-muted-foreground">{icon}</div>
        </div>
      </CardContent>
    </Card>
  )
}

function StatusBadge({ status }: { readonly status: Campaign['status'] }) {
  const config: Record<Campaign['status'], { label: string; className: string }> = {
    active: {
      label: 'Active',
      className: 'border-transparent bg-emerald-500/12 text-emerald-300 ',
    },
    paused: {
      label: 'Paused',
      className: 'border-transparent bg-amber-500/12 text-amber-300 ',
    },
    draft: {
      label: 'Draft',
      className: 'border-transparent bg-[rgba(255,255,255,0.05)] text-[var(--color-silver)] dark:text-[var(--color-silver)]',
    },
    completed: {
      label: 'Completed',
      className: 'border-transparent bg-[rgba(28,63,255,0.12)] text-[var(--color-cobalt-bright)] ',
    },
    deleted: {
      label: 'Deleted',
      className: 'border-transparent bg-red-500/12 text-red-300 ',
    },
  }
  const { label, className } = config[status]
  return <Badge className={className}>{label}</Badge>
}

type CampaignDetailProps = {
  readonly campaignId: string
  readonly onBack: () => void
}

export function CampaignDetail({ campaignId, onBack }: CampaignDetailProps) {
  const { data: campaign, isLoading, error } = useCampaign(campaignId)
  const { data: stats } = useCampaignStats(campaignId)
  const { data: campaignLeadsData } = useCampaignLeads(campaignId)
  const campaignLeads = campaignLeadsData?.leads ?? []
  const campaignLeadsTotal = campaignLeadsData?.total ?? campaignLeads.length
  const activate = useActivateCampaign()
  const pause = usePauseCampaign()
  const removeLead = useRemoveLeadFromCampaign()
  const [editOpen, setEditOpen] = useState(false)
  const [addLeadsOpen, setAddLeadsOpen] = useState(false)
  const [editSequenceOpen, setEditSequenceOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  // Plan tier — used by PlanGate around feature-tier UI.
  const plan = usePlan()
  // Taha-only gate for internal debug affordances. As of 2026-06-06
  // (pre-launch), Preview AI Messages and Test Mode are hidden from EVERYONE
  // except Taha — even Eli/TJ in the staff allowlist. They're powerful
  // debug tools that confuse new customers (Preview spends LLM credits on
  // demand; Test Mode bypasses activity windows and step delays). Taha keeps
  // them visible for live customer-support investigations during launch.
  const { user: clerkUser } = useUser()
  const isInternalAdmin = isTahaOnly(clerkUser?.id)

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <div className="h-12 w-64 rounded-lg bg-muted/30 animate-pulse" />
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 rounded-xl bg-muted/30 animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (error || !campaign) {
    return (
      <div className="flex flex-col items-center gap-4 py-16">
        <p className="text-lg font-medium">Campaign not found</p>
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to campaigns
        </Button>
      </div>
    )
  }

  const replyRate =
    campaign.sent_count > 0
      ? ((campaign.replied_count / campaign.sent_count) * 100).toFixed(1)
      : '0.0'

  const meetingRate =
    campaign.replied_count > 0
      ? ((campaign.meeting_count / campaign.replied_count) * 100).toFixed(1)
      : '0.0'

  const healthColor =
    stats?.account_health === 'good'
      ? 'bg-green-500'
      : stats?.account_health === 'warning'
        ? 'bg-yellow-500'
        : 'bg-red-500'


  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="self-start -ml-2">
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to campaigns
        </Button>

        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-2xl font-bold">{campaign.name}</h2>
              <StatusBadge status={campaign.status} />
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Created {fmtDate(campaign.created_at, 'MMM d, yyyy')} &middot;{' '}
              Updated {fmtDistanceToNow(campaign.updated_at, { addSuffix: true })}
            </p>
          </div>

          {campaign.status !== 'deleted' && (
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
              {isInternalAdmin && (
                <PlanGate requiredPlan="pro" currentPlan={plan}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPreviewOpen(true)}
                    title="See sample AI messages for this campaign's leads (staff only)"
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Preview AI Messages
                  </Button>
                </PlanGate>
              )}
              {isInternalAdmin && <TestModeButton campaign={campaign} />}
              <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
                <Pencil className="h-4 w-4 mr-1" />
                Edit
              </Button>
              {campaign.status === 'active' ? (
                <Button
                  variant="outline"
                  onClick={() => pause.mutate(campaign.id)}
                  disabled={pause.isPending}
                >
                  <Pause className="h-4 w-4 mr-1" />
                  {pause.isPending ? 'Pausing...' : 'Pause Campaign'}
                </Button>
              ) : campaign.status !== 'completed' ? (
                <Button
                  onClick={() => activate.mutate(campaign.id)}
                  disabled={activate.isPending}
                >
                  <Play className="h-4 w-4 mr-1" />
                  {activate.isPending ? 'Activating...' : 'Activate Campaign'}
                </Button>
              ) : null}
              <Button
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
              </div>
              {isInternalAdmin && campaign.test_mode && (
                <p className="text-xs font-medium text-amber-300 max-w-md text-right">
                  Test Mode auto-disables in the time shown on the button. Activity window
                  + step delays bypassed; per-lead retry, daily limit, and 3-action-per-tick
                  cap still apply.
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
        <StatCard
          label="Total Leads"
          value={campaign.leads_count}
          icon={<Users className="h-5 w-5" />}
        />
        <StatCard
          label="Connections Sent"
          value={campaign.sent_count}
          icon={<UserPlus className="h-5 w-5" />}
        />
        <StatCard
          label="Messages Sent"
          value={campaign.contacted_count}
          icon={<Send className="h-5 w-5" />}
        />
        <StatCard
          label="Replies"
          value={campaign.replied_count}
          icon={<MessageSquare className="h-5 w-5" />}
        />
        <StatCard
          label="Reply Rate"
          value={`${replyRate}%`}
          icon={<Activity className="h-5 w-5" />}
        />
        <StatCard
          label="Meetings"
          value={campaign.meeting_count}
          icon={<Calendar className="h-5 w-5" />}
        />
        <StatCard
          label="Meeting Rate"
          value={`${meetingRate}%`}
          icon={<Activity className="h-5 w-5" />}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Safety gauge */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Daily Activity</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {/* Daily volume is platform-managed; the per-campaign limit is
                deliberately not shown (2026-07-27 owner directive). */}
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Messages today</span>
              <span className="font-medium">{stats?.messages_today ?? 0}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Sends go out weekdays between 9:00 AM and 5:00 PM New York time.
              Weekend sending can be switched on in Settings, Safety.
            </p>

            <div className="flex items-center justify-between text-sm pt-1">
              <span className="text-muted-foreground">Account health</span>
              <div className="flex items-center gap-1.5">
                <div className={`h-2 w-2 rounded-full ${healthColor}`} />
                <span className="font-medium capitalize">
                  {stats?.account_health ?? 'good'}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sequence visualization */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold">Sequence</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setEditSequenceOpen(true)}>
                <Settings2 className="h-3.5 w-3.5 mr-1" />
                Edit
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-0">
              {campaign.sequence_config.steps.map((step, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="flex flex-col items-center">
                    <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                      {index + 1}
                    </div>
                    {index < campaign.sequence_config.steps.length - 1 && (
                      <div className="w-px h-5 bg-border" />
                    )}
                  </div>
                  <div className="pb-2 pt-0.5">
                    <p className="text-sm font-medium">
                      {STEP_TYPE_LABELS[step.type] ?? step.type}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {STRATEGY_LABELS[step.strategy] ?? step.strategy}
                      {step.delay_days > 0 && `, +${step.delay_days} days`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leads table */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">
              Leads ({campaignLeadsTotal})
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setAddLeadsOpen(true)}>
              <UserPlus className="h-3.5 w-3.5 mr-1" />
              Add Leads
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {campaignLeads.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              No leads found for this campaign.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Messages</TableHead>
                  <TableHead>Last Activity</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {campaignLeads.map((lead) => {
                  const displayName =
                    lead.full_name ?? `${lead.first_name} ${lead.last_name}`
                  const lastActivity = fmtDistanceToNow(lead.last_activity_at, { addSuffix: true })

                  return (
                    <TableRow key={lead.id}>
                      <TableCell>
                        <div>
                          {lead.linkedin_url ? (
                            <a
                              href={lead.linkedin_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-sm hover:underline hover:text-primary"
                            >
                              {displayName}
                            </a>
                          ) : (
                            <p className="font-medium text-sm">{displayName}</p>
                          )}
                          <p className="text-xs text-muted-foreground">
                            {lead.job_title ?? ''}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{lead.company ?? '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs capitalize">
                          {lead.status.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {lead.messages_sent ?? 0}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {lastActivity}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                          onClick={() => removeLead.mutate({ campaignId: campaign.id, leadId: lead.id })}
                          disabled={removeLead.isPending}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
          {campaignLeads.length > 0 && campaignLeads.length < campaignLeadsTotal && (
            <div className="py-2 text-center text-xs text-muted-foreground border-t">
              Showing first {campaignLeads.length} of {campaignLeadsTotal} leads
            </div>
          )}
        </CardContent>
      </Card>

      <EditCampaignDialog campaign={campaign} open={editOpen} onOpenChange={setEditOpen} />
      <AddLeadsDialog campaignId={campaign.id} open={addLeadsOpen} onOpenChange={setAddLeadsOpen} />
      <EditSequenceDialog
        campaignId={campaign.id}
        currentSequence={campaign.sequence_config}
        open={editSequenceOpen}
        onOpenChange={setEditSequenceOpen}
      />
      <DeleteCampaignDialog
        campaignName={campaign.name}
        campaignId={campaign.id}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onDeleted={onBack}
      />
      <PreviewMessagesDialog
        campaignId={campaign.id}
        campaignName={campaign.name}
        open={previewOpen}
        onOpenChange={setPreviewOpen}
      />
    </div>
  )
}
